import { subMonths } from "date-fns";
import { useEffect } from "react";

import { DATE_FORMATS_FNS, statusFilters } from "components";
import { useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { typedQueryClient, usePrevious, useTypedQuery } from "hooks";
import { DEFAULT_PIN_TYPES } from "modules/WarrantyDashboard/components/WarrantyDashboardTable/hooks";
import { ORDER_BY, WARRANTY_TIME_PERIODS } from "modules/WarrantyDashboard/hooks";
import { WarrantyQueryKeys } from "modules/WarrantyDashboard/queryKeys";
import { WARRANTY_STORAGE_KEYS } from "modules/WarrantyDashboard/storageKeys";
import { FAKE_STATUSES } from "utils/appointmentUtils";
import { PAGE_SIZE, getPreference } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { DealerLocation } from "models";
import type { WarrantyDashboardFilters } from "modules/WarrantyDashboard/queryKeys";

export const getDefaultWarrantyDashboardFilters = (selectedLocation?: DealerLocation | null): WarrantyDashboardFilters => {
  const defaultDateFrom = formatDate(subMonths(new Date(), 1), DATE_FORMATS_FNS.ISOFormatEndOfDay);
  const defaultDateTo = formatDate(new Date(), DATE_FORMATS_FNS.ISOFormatEndOfDay);
  const appointmentStatusIdentifiers = statusFilters.filter(status => !FAKE_STATUSES.includes(status.identifier));

  return {
    appointment_statuses: getPreference(WARRANTY_STORAGE_KEYS.AppointmentStatusIdentifiersPreference, appointmentStatusIdentifiers),
    brands: getPreference(WARRANTY_STORAGE_KEYS.BrandsPreference, null),
    claimed: getPreference(WARRANTY_STORAGE_KEYS.ClaimedPreference, false),
    deleted: getPreference(WARRANTY_STORAGE_KEYS.DeletedPreference, false),
    pin_date_from: getPreference(WARRANTY_STORAGE_KEYS.PinDateFromPreference, defaultDateFrom),
    pin_date_to: getPreference(WARRANTY_STORAGE_KEYS.PinDateToPreference, defaultDateTo),
    appointment_date_from: getPreference(WARRANTY_STORAGE_KEYS.AppointmentDateFromPreference, defaultDateFrom),
    appointment_date_to: getPreference(WARRANTY_STORAGE_KEYS.AppointmentDateToPreference, defaultDateTo),
    dealer_ids: getPreference(WARRANTY_STORAGE_KEYS.DealerPreference, []),
    location_ids: getPreference(WARRANTY_STORAGE_KEYS.LocationPreference, selectedLocation ? [selectedLocation.id] : []),
    mechanic_fixed: getPreference(WARRANTY_STORAGE_KEYS.MechanicFixedPreference, null),
    order_by: getPreference(WARRANTY_STORAGE_KEYS.OrderByPreference, ORDER_BY.AppointmentDateDescending),
    page: 1,
    pin_status_id: getPreference(WARRANTY_STORAGE_KEYS.PinStatusPreference, null),
    pin_type_id: getPreference(WARRANTY_STORAGE_KEYS.PinTypePreference, DEFAULT_PIN_TYPES),
    search_term: "",
    status: getPreference(WARRANTY_STORAGE_KEYS.QuestionStatusPreference, null),
    warranty_type_id: getPreference(WARRANTY_STORAGE_KEYS.WarrantyTypePreference, null),
    claim_order_by: getPreference(WARRANTY_STORAGE_KEYS.ClaimSortPreference, null),
    time_period: getPreference(WARRANTY_STORAGE_KEYS.TimePeriodPreference, WARRANTY_TIME_PERIODS.LastMonth),
    appointment_time_period: getPreference(WARRANTY_STORAGE_KEYS.AppointmentTimePeriodPreference, WARRANTY_TIME_PERIODS.LastMonth),
    order_by_location: getPreference(WARRANTY_STORAGE_KEYS.LocationOrderByPreference, null),
    page_size: getPreference(WARRANTY_STORAGE_KEYS.PageSizePreference, PAGE_SIZE.PageSize25),
    pin_status_refresh_timestamp: null
  };
};

export const useGetWarrantyDashboardFilters = () => {
  const { selectedLocation } = useSelectedLocation();
  const previousLocation = usePrevious(selectedLocation?.id);

  useEffect(() => {
    if (!previousLocation && selectedLocation && !getPreference<boolean>(WARRANTY_STORAGE_KEYS.LocationPreference, false))
      typedQueryClient.setQueryData(WarrantyQueryKeys.dashboardFilters, {
        ...getDefaultWarrantyDashboardFilters(selectedLocation),
        location_ids: [selectedLocation.id]
      });
  }, [selectedLocation?.id, previousLocation]);

  const { data: filters, isFetching } = useTypedQuery({
    queryKey: WarrantyQueryKeys.dashboardFilters,
    initialData: getDefaultWarrantyDashboardFilters(selectedLocation)
  });

  return { filters, isLoading: isFetching };
};

export const setWarrantyDashboardFilters = (newFilters: WarrantyDashboardFilters) => {
  const filters = typedQueryClient.getQueryData(WarrantyQueryKeys.dashboardFilters);
  if (!filters) return;
  typedQueryClient.setQueryData(WarrantyQueryKeys.dashboardFilters, { ...filters, ...newFilters });
};
